import { getLeadCompound } from "@/modules/lead-page/api/lead-page.api";
import { Box, Dialog, IconButton, Typography, useTheme } from "@mui/material";
import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import LocationActions from "@/modules/lead-page/new/components/sidebar/fields/linked-location/location-actions";

import LoadingSkeleton from "@/modules/common/components/ui/loadingSkeleton";
import { X, ZoomIn, ZoomOut } from "lucide-react";

interface CompoundInfoProps {
  compoundId: number | undefined | null;
}

const CompoundInfo = ({ compoundId }: CompoundInfoProps) => {
  const [isImageOpen, setIsImageOpen] = useState(false);
  const [imageScale, setImageScale] = useState(1);
  const theme = useTheme();

  const { data: compound, isLoading } = useQuery({
    queryKey: ["lead-compound", compoundId],
    queryFn: () => getLeadCompound(compoundId!),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    enabled: !!compoundId,
  });

  const handleZoomIn = () => {
    setImageScale((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setImageScale((prev) => Math.max(prev - 0.2, 0.5));
  };

  const handleCloseImage = () => {
    setIsImageOpen(false);
    setImageScale(1);
  };

  const renderCompoundInfo = useCallback(() => {
    if (!compoundId)
      return (
        <Typography
          sx={{
            color: theme.nawy.colors.semantic.text.primary,
            ...theme.nawy.typography.english.variants.body.md.regular,
          }}
        >
          No Linked Compound found
        </Typography>
      );

    if (isLoading)
      return (
        <Box sx={{ width: "100%" }}>
          <LoadingSkeleton
            columns={1}
            rows={1}
            widths={[190]}
            heights={[100]}
            variants={["rectangular"]}
          />
        </Box>
      );

    if (compound?.data)
      return (
        <>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Typography
              sx={{
                color: theme.nawy.colors.semantic.text.primary,
                ...theme.nawy.typography.english.variants.body.md.regular,
              }}
            >
              {compound?.data?.name}
            </Typography>
            <Typography
              sx={{
                color: theme.nawy.colors.semantic.text.primary,
                ...theme.nawy.typography.english.variants.body.md.regular,
              }}
            >
              {compound?.data?.area?.en}
            </Typography>
          </Box>
          {/* Clickable Image */}
          <Box
            component="img"
            src={compound?.data?.map_path}
            alt="Compound Image"
            sx={{
              width: "100%",
              borderRadius: theme.nawy.radius.lg,
              cursor: "pointer",
            }}
            onClick={() => setIsImageOpen(true)}
          />
          <Dialog open={isImageOpen} onClose={handleCloseImage} fullWidth>
            <Box
              sx={{
                position: "relative",
                bgcolor: "black",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                p: 2,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  display: "flex",
                  gap: 1,
                  zIndex: 1,
                }}
              >
                <IconButton
                  onClick={handleZoomIn}
                  sx={{ color: "white", bgcolor: "rgba(255,255,255,0.1)" }}
                >
                  <ZoomIn />
                </IconButton>
                <IconButton
                  onClick={handleZoomOut}
                  sx={{ color: "white", bgcolor: "rgba(255,255,255,0.1)" }}
                >
                  <ZoomOut />
                </IconButton>
                <IconButton
                  onClick={handleCloseImage}
                  sx={{ color: "white", bgcolor: "rgba(255,255,255,0.1)" }}
                >
                  <X />
                </IconButton>
              </Box>

              <Box
                sx={{
                  width: "100%",
                  height: "calc(100vh - 100px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "auto",
                }}
              >
                <Box
                  component="img"
                  src={compound?.data?.map_path}
                  alt="Compound Image"
                  sx={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                    transform: `scale(${imageScale})`,
                    transition: "transform 0.2s ease-in-out",
                  }}
                />
              </Box>
            </Box>
          </Dialog>
          <LocationActions
            latitude={compound?.data?.latitude}
            longitude={compound?.data?.longitude}
            id={compound?.data?.id}
          />
        </>
      );
  }, [compound, isLoading, imageScale, isImageOpen]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      {renderCompoundInfo()}
    </Box>
  );
};

export default CompoundInfo;
